import { observer } from "mobx-react-lite";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { structure } from "@/endpoints/dryp/structure";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useRainData } from "@/hooks/useRainData";
import { StructureHeader } from "./components/StructureHeader";
import { StructureDetails } from "./components/StructureDetails";
import { StructureEvents } from "./components/StructureEvents";
import { HydraulicPerformance } from "./components/HydraulicPerformance";
import { Notes } from "./Notes";
import { Box } from "./components/Box";
import { StructureTimePeriodsValue } from "@/config/date";
import { DateRange } from "react-day-picker";
import { getDateRangeFromTimePeriod } from "@/components/TimePeriod/helper";
import { StructureAlarms } from "./components/StructureAlarms";
import { StructureProperties } from "./components/StructureProperties";

const StructureLoading = () => (
	<div className="p-6">
		<div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
			<div className="lg:col-span-3">
				<Box className="p-4 space-y-4">
					<Skeleton className="h-6 w-2/3" />
					<Skeleton className="h-48 w-full rounded-lg" />
					<Skeleton className="h-5 w-1/2" />
					<div className="grid grid-cols-2 gap-3">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</div>
				</Box>
			</div>
			<div className="lg:col-span-9">
				<Skeleton className="h-64 w-full rounded-xl" />
			</div>
		</div>
	</div>
);

const DEFAULT_TIME_PERIOD: StructureTimePeriodsValue = "last30d";

type StructureViewProps = {
	structure: Dryp.Structure;
	onRefresh?: () => void;
};

const StructureView = ({ structure, onRefresh }: StructureViewProps) => {
	const [hydraulicExpanded, setHydraulicExpanded] = useState(false);
	const [timePeriod, setTimePeriod] = useState<StructureTimePeriodsValue>(DEFAULT_TIME_PERIOD);
	const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
		getDateRangeFromTimePeriod(DEFAULT_TIME_PERIOD),
	);

	const handleTimePeriodChange = (value: StructureTimePeriodsValue, range: DateRange | undefined) => {
		setTimePeriod(value);
		setDateRange(range);
	};

	// Convert DateRange to ISO strings for components
	const from = dateRange?.from ? DateTime.fromJSDate(dateRange.from).toUTC().toISO()! : "";
	const to = dateRange?.to ? DateTime.fromJSDate(dateRange.to).toUTC().toISO()! : "";

	const { rainOverlay, sourceInfo } = useRainData(
		structure.coordinate.latitude,
		structure.coordinate.longitude,
		from,
		to,
		timePeriod,
	);

	return (
		<>
			<StructureHeader
				structure={structure}
				timePeriod={timePeriod}
				onTimePeriodChange={handleTimePeriodChange}
			/>
			<div className="p-3 md:p-4 lg:p-6">
				<div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
					{/* Left column: Details, Events, Notes */}
					<div className="w-full lg:w-64 2xl:w-80 shrink-0 space-y-4 lg:space-y-6">
						<StructureDetails structure={structure} onRefresh={onRefresh} />
						<StructureAlarms structureId={structure.id as Dryp.StructureId} isActive={structure.isActive} />

						<div className={cn(hydraulicExpanded ? "block" : "block 2xl:hidden")}>
							<StructureEvents structureId={structure.id} from={from} to={to} />
						</div>
						{!hydraulicExpanded && (
							<div className="block 2xl:hidden">
								<Notes structureId={structure.id} inline />
							</div>
						)}
						<StructureProperties structure={structure} onConfigured={onRefresh} />
					</div>

					{/* Middle column: Events + Notes (only on 2xl when not expanded) */}
					<div className={cn("w-80 shrink-0 space-y-6", hydraulicExpanded ? "hidden" : "hidden 2xl:block")}>
						<StructureEvents structureId={structure.id} from={from} to={to} />
						<Notes structureId={structure.id} inline />
					</div>

					{/* Right column: Hydraulic Performance */}
					<div className="w-full lg:flex-1 min-w-0 space-y-4 lg:space-y-6">
						<HydraulicPerformance
							structureId={structure.id}
							hasIAndI={structure.hasIAndI}
							hasBlockage={structure.hasBlockage}
							from={from}
							to={to}
							timePeriod={timePeriod}
							rainOverlay={rainOverlay}
							sourceInfo={sourceInfo}
							structureLat={structure.coordinate.latitude}
							structureLon={structure.coordinate.longitude}
							structureName={structure.name}
							isExpanded={hydraulicExpanded}
							onExpandChange={setHydraulicExpanded}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export const Structure = observer(() => {
	const { id } = useParams<{ id: string }>();
	const structureId = id as Dryp.StructureId;
	const structureApi = useAsyncStateStore(structure.getAllStructures);

	const fetchStructure = () => {
		if (structureId) {
			structureApi.fetch({ pageNumber: 0, pageSize: 1 }, { structureIds: [structureId] });
		}
	};

	useEffect(() => {
		fetchStructure();
	}, [structureId]);

	if (structureApi.error) {
		return <div className="p-6 text-red-500">Error loading structure.</div>;
	}

	if (!structureApi.loaded) {
		return <StructureLoading />;
	}

	const structures = structureApi.data?.items || [];
	const structureData = structures.find(s => s.id.toString() === structureId);

	if (!structureData) {
		return <div className="p-6 text-gray-500">This structure does not exist.</div>;
	}

	return <StructureView structure={structureData} onRefresh={fetchStructure} />;
});
